import { createContext, useCallback, useContext, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X, Loader2 } from "lucide-react";

/**
 * Branded confirmation dialog with the Shariti dark/orange theme.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: "Supprimer cet utilisateur ?",
 *     description: "Cette action est irréversible.",
 *     confirmLabel: "Supprimer",
 *     tone: "danger",
 *   });
 *   if (ok) { ...do destructive thing... }
 *
 * Wrap your app with `<ConfirmProvider>...</ConfirmProvider>` once at the top level.
 * Honors Escape to cancel and click outside to cancel.
 *
 * `onConfirm` may return a Promise — the dialog will show a loader, await it, then close.
 */
const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(false);

    const confirm = useCallback((opts) => {
        return new Promise((resolve) => {
            setState({ ...opts, _resolve: resolve });
        });
    }, []);

    const close = (result) => {
        if (state?._resolve) state._resolve(result);
        setState(null);
        setLoading(false);
    };

    const handleConfirm = async () => {
        if (!state) return;
        if (typeof state.onConfirm === "function") {
            try {
                setLoading(true);
                await state.onConfirm();
                close(true);
            } catch {
                setLoading(false);
                // Keep modal open if onConfirm threw — caller decides next step via toast etc.
            }
        } else {
            close(true);
        }
    };

    const tone = state?.tone || "default";
    const confirmClasses =
        tone === "danger"
            ? "bg-red-500/90 hover:bg-red-500 text-white"
            : "bg-shariti-orange hover:bg-shariti-orange-light text-white orange-glow";
    const iconWrap =
        tone === "danger"
            ? "bg-red-500/15 text-red-400"
            : "bg-shariti-orange/15 text-shariti-orange-light";

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            <AnimatePresence>
                {state && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
                        onClick={() => !loading && close(false)}
                        data-testid="confirm-backdrop"
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 16, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 16, scale: 0.96 }}
                            transition={{ duration: 0.18 }}
                            onClick={(e) => e.stopPropagation()}
                            className="relative w-full max-w-md glass-card rounded-3xl p-5 sm:p-6 border border-white/10 shadow-2xl shadow-black/60"
                            role="dialog"
                            aria-modal="true"
                            data-testid="confirm-dialog"
                        >
                            <button
                                onClick={() => !loading && close(false)}
                                disabled={loading}
                                aria-label="Fermer"
                                data-testid="confirm-close"
                                className="absolute top-3 right-3 w-8 h-8 rounded-xl flex items-center justify-center text-white/50 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-40"
                            >
                                <X className="w-4 h-4" />
                            </button>

                            <div className="flex items-start gap-3 sm:gap-4 mb-4">
                                <div
                                    className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${iconWrap}`}
                                >
                                    <AlertTriangle className="w-5 h-5" />
                                </div>
                                <div className="flex-1 min-w-0 pr-4">
                                    <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-1">
                                        {tone === "danger" ? "Action irréversible" : "Confirmation"}
                                    </p>
                                    <h3 className="text-lg sm:text-xl font-extrabold text-white tracking-tight leading-tight">
                                        {state.title || "Es-tu sûr·e ?"}
                                    </h3>
                                </div>
                            </div>

                            {state.description && (
                                <p className="text-sm text-white/70 font-light leading-relaxed mb-5 sm:mb-6">
                                    {state.description}
                                </p>
                            )}

                            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 sm:justify-end">
                                <button
                                    type="button"
                                    onClick={() => close(false)}
                                    disabled={loading}
                                    data-testid="confirm-cancel"
                                    className="px-5 py-2.5 rounded-2xl glass-card text-white/80 font-bold text-sm hover:text-white hover:border-white/20 transition-all disabled:opacity-40"
                                >
                                    {state.cancelLabel || "Annuler"}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleConfirm}
                                    disabled={loading}
                                    data-testid="confirm-accept"
                                    className={`px-5 py-2.5 rounded-2xl font-extrabold text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-60 ${confirmClasses}`}
                                >
                                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                                    {state.confirmLabel || "Confirmer"}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </ConfirmContext.Provider>
    );
}

export function useConfirm() {
    const ctx = useContext(ConfirmContext);
    if (!ctx) throw new Error("useConfirm must be inside <ConfirmProvider>");
    return ctx;
}
